import { action, computed, makeObservable, observable } from "mobx";
import { inject, injectable } from "inversify";
import { FiltersStore } from "../../services/filtersStore";
import { hasAnyPermission } from "@whatslly/commons/platformPermissions";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import type { TuvisIconNamesType } from "@whatslly/types/UI-V2/BaseElements/Svg/TuvisIconNamesType";
import { NavItemType } from "@whatslly/ui-components/SideBarNavMenu";
import type { IconType } from "react-icons";
import { UserPermissions } from "@whatslly/commons/granularPermissions";
import type { IOrganizationStore } from "../../services/IOrganizationStore";
import { InjectOrganizationStore } from "../../services/IOrganizationStore";
import { FaBullhorn, FaMobile, FaPeopleGroup, FaWhatsapp, FaUserSecret } from "react-icons/fa6";
import {
  LuLayoutList,
  LuSettings,
  LuLayoutDashboard,
  LuFilter,
  LuBot,
  LuWorkflow,
  LuChartBar,
  LuKeyRound,
  LuList,
  LuSend,
  LuHash,
  LuLink,
  LuShieldAlert,
  LuListChecks,
  LuShieldCheck,
  LuTriangleAlert,
  LuMegaphone,
  LuSearch,
  LuLayoutTemplate,
  LuMessageSquare,
  LuMic,
  LuScrollText,
  LuLock,
  LuSparkles,
  LuMessagesSquare,
  LuRadio,
  LuArchive,
  LuFileScan,
  LuMail,
  LuPhoneOff,
  LuLaptopMinimalCheck,
  LuHardDrive,
  LuCloud,
  LuPuzzle,
} from "react-icons/lu";
import { RiUserSettingsLine, RiPagesFill } from "react-icons/ri";
import { GrAppsRounded } from "react-icons/gr";
import { FiUser, FiUsers } from "react-icons/fi";
import { IoLogoWechat, IoBrowsersOutline, IoApps, IoCheckmarkDoneCircleOutline } from "react-icons/io5";
import { ChannelsPageStore } from "../ChannelsPage/ChannelsPageStore";
import { remoteSyncChannelsConfig } from "../ChannelsPage/remoteSyncChannelsConfig";
import { UrlStore } from "../../services/urlStore";
import { OrgConfigsStore } from "../../services/OrgConfigsStore";
import { canUserLoginToDesktop } from "@whatslly/commons/desktopPermissions";
import { TAB_NAMES, TAB_URL_PATHS } from "../AnalyticsDashboard/AnalyticsDashboardStore";
import { DESTINATION_INTEGRATION_TYPES } from "@whatslly/types/messages-server/destinations";

type NavMenuItem = NavItemType & { children?: NavMenuItem[]; permission: boolean | undefined; defaultQuickAction?: boolean };
type NavigationTreeItem = NavMenuItem & { subtree?: NavigationTree; defaultQuickAction?: boolean };
type NavigationTree = {
  items: NavigationTreeItem[];
  dict: Record<string, NavigationTreeItem>;
  defaultUrl: string;
};

@injectable()
export class NavMenuStore {
  private t = (key: string, _params?: any) => key;

  constructor(
    @inject(InjectUserStore) private userStore: IUserStore,
    @inject(InjectOrganizationStore) private readonly orgStore: IOrganizationStore,
    @inject(FiltersStore) private readonly filtersStore: FiltersStore,
    @inject(UrlStore) private readonly urlStore: UrlStore,
    @inject(ChannelsPageStore) private readonly channelsPageStore: ChannelsPageStore,
    @inject(OrgConfigsStore) private readonly orgConfigsStore: OrgConfigsStore
  ) {
    makeObservable(this, {
      t: observable.ref,
      tree: computed,
      availablePaths: computed,
      init: action,
      hasDesktopPermission: computed,
    } as any);
  }

  init(t: (key: string) => string) {
    this.t = t;
  }

  getRedirectTo(items: NavigationTree["items"]) {
    const redirectToProp = new URLSearchParams(window.location.search)?.get("redirectTo");
    if (!redirectToProp) return;
    const path = redirectToProp.split("?")[0];
    const shardedSearch = redirectToProp.split("?").filter((part, i) => i !== 0);
    const searchAsString = shardedSearch.join("?");
    const flattedItems = items.flatMap((option) => option.subtree?.items ?? option);
    let redirectTo = flattedItems.find((option) => option.to.startsWith(path) && option.permission)?.to;
    if (redirectTo && searchAsString.length > 0) redirectTo += `?${searchAsString}`;
    return redirectTo;
  }

  get pathNameDict() {
    const pathname = this.urlStore.pathname;
    const keys = Object.keys(this.tree.dict).sort((a, b) => b.length - a.length);
    const matched = keys.find((key) => pathname === key || pathname.startsWith(`${key}/`));
    if (matched) return matched;
    const parts = pathname.split("/").filter(Boolean);
    return parts.length ? `/${parts[0]}` : "/";
  }

  get menuDict() {
    return this.tree.dict[this.pathNameDict];
  }

  get isCurrentDictActive() {
    return this.menuDict?.to === this.urlStore.pathname || this.menuDict?.children?.some((child) => child.to === this.urlStore.pathname);
  }

  get isMenuLoading() {
    return (
      this.orgConfigsStore.areOrgConfigsLoading && Boolean(this.filtersStore.orgId && this.filtersStore.orgId !== this.userStore.user?.organizationId)
    );
  }

  get isSubMenuLoading() {
    return this.isCurrentDictActive && this.menuDict?.areChildrenLoading ? this.menuDict.areChildrenLoading() : false;
  }

  private getDefaultUrl(items: NavigationTree["items"]) {
    const redirectTo = this.getRedirectTo(items);
    if (redirectTo) return redirectTo;

    const urlWithPermission = items.flatMap((option) => option.subtree?.items ?? option).find((option) => option.permission);
    if (urlWithPermission) {
      const urlParams = new URLSearchParams();

      if (this.filtersStore.orgId) {
        urlParams.set("orgId", this.filtersStore.orgId);
      }

      if (this.filtersStore.accountId) {
        urlParams.set("accountId", this.filtersStore.accountId);
      }

      return urlParams.size > 0 ? `${urlWithPermission.to}?${urlParams}` : urlWithPermission.to;
    }
    return `/login${location.search || `?error=${decodeURIComponent(this.t("login.no_sufficient_permissions"))}`}`;
  }

  private get hasDesktopPermission() {
    return canUserLoginToDesktop(this.userStore.user);
  }

  private normalizeNavigationTree(original: NavMenuItem[]): NavigationTree {
    const items = original
      .map((item) => {
        if (item.children) {
          const subtree = this.normalizeNavigationTree(item.children);
          return {
            ...item,
            permission: item.permission && subtree.items.some((child) => child.permission),
            subtree,
          };
        }
        return item as NavMenuItem & { children: undefined };
      })
      .filter((item) => item.permission);
    const dict = Object.fromEntries(items.map((option) => [option.to, option]));
    const defaultUrl = this.getDefaultUrl(items);
    return { items, dict, defaultUrl };
  }

  get tree() {
    return this.normalizeNavigationTree([
      {
        testId: "home",
        to: `/home`,
        iconName: "home" as TuvisIconNamesType,
        title: this.t("home.title"),
        permission: this.userStore.user?.permissions !== UserPermissions.none.toString() || (this.userStore.user?.accounts ?? []).length > 0,
        hidden: true,
      },
      {
        testId: "/analytics/dashboard",
        to: `/analytics/dashboard`,
        iconName: "engagement" as TuvisIconNamesType,
        title: this.t("analytics_dashboard.title"),
        permission: hasAnyPermission(this.userStore.user, [UserPermissions["analytics.view"]]),
        category: "analytics",
        children: (() => {
          const ANALYTICS_DASHBOARD_TAB_ORDER: TAB_NAMES[] = [
            TAB_NAMES.UserAnalytics,
            TAB_NAMES.UsersConversationsStatus,
            TAB_NAMES.EngagementInsights,
            TAB_NAMES.CallRecordingsInsights,
          ];

          const ANALYTICS_DASHBOARD_TAB_TITLE_KEY: Record<TAB_NAMES, string> = {
            [TAB_NAMES.UserAnalytics]: "user_analytics.title",
            [TAB_NAMES.UsersConversationsStatus]: "Users Conversations Status",
            [TAB_NAMES.EngagementInsights]: "engagement.title",
            [TAB_NAMES.CallRecordingsInsights]: "call_recordings_insights.title",
          };
          const cfg = this.orgConfigsStore.configs;
          const configReady = cfg != null;
          const whenReady = (p: boolean) => (configReady ? p : true);
          const user = this.userStore.user;
          const analyticsDashboardTabAllowed = {
            [TAB_NAMES.EngagementInsights]: whenReady(hasAnyPermission(user, [UserPermissions["analytics.view"]])),
            [TAB_NAMES.CallRecordingsInsights]: whenReady(Boolean(cfg?.allow_mobile_call_recording || cfg?.allow_twilio)),
            [TAB_NAMES.UserAnalytics]: whenReady(Boolean(cfg?.allow_user_analytics)),
            [TAB_NAMES.UsersConversationsStatus]: whenReady(
              Boolean(cfg?.track_filter_analytics && hasAnyPermission(user, [UserPermissions["analytics.view"]]))
            ),
          } satisfies Record<TAB_NAMES, boolean>;
          return ANALYTICS_DASHBOARD_TAB_ORDER.map((tab) => ({
            testId: `/analytics/dashboard/${TAB_URL_PATHS[tab]}`,
            to: `/analytics/dashboard/${TAB_URL_PATHS[tab]}`,
            title: this.t(ANALYTICS_DASHBOARD_TAB_TITLE_KEY[tab]),
            permission: hasAnyPermission(user, [UserPermissions["analytics.view"]]) && analyticsDashboardTabAllowed[tab],
            category: "analytics" as const,
          }));
        })(),
      },
      {
        testId: "/analytics/adoption",
        to: `/analytics/adoption`,
        iconName: "adoption" as TuvisIconNamesType,
        title: this.t("adoption.title"),
        permission: hasAnyPermission(this.userStore.user, [UserPermissions["analytics.view"]]),
        hidden: true,
      },
      {
        testId: "/admin/users",
        to: `/admin/users`,
        icon: FiUser,
        title: this.t("users_page.title"),
        permission: hasAnyPermission(this.userStore.user, [UserPermissions["user_management.view"]]),
        category: "userManagement",
        defaultQuickAction: true,
      },
      {
        testId: "/admin/groups",
        to: `/admin/groups`,
        icon: FiUsers,
        title: this.t("user_groups.title"),
        permission: hasAnyPermission(this.userStore.user, [UserPermissions["user_management.view_groups"]]),
        category: "userManagement",
        defaultQuickAction: true,
      },
      {
        testId: "/admin/audit-log",
        to: `/admin/audit-log`,
        icon: LuLayoutList,
        title: this.t("audit_log.title"),
        permission: hasAnyPermission(this.userStore.user, [UserPermissions["audit_log.view"]]),
        category: "securityCompliance",
        defaultQuickAction: true,
      },
      {
        testId: "/desktop-policies",
        to: `/desktop-policies`,
        iconName: "website" as TuvisIconNamesType,
        title: this.t("url_policies_page.title"),
        permission: hasAnyPermission(this.userStore.user, [UserPermissions["url_policy.update"], UserPermissions["url_policy.view"]]),
        category: "securityCompliance",
      },
      {
        testId: "/tuvis-browser",
        to: `/tuvis-browser`,
        icon: IoBrowsersOutline,
        title: this.t("browser_url_policies_page.nav_group_title"),
        permission:
          hasAnyPermission(this.userStore.user, [
            UserPermissions["browser_url_policy.update"],
            UserPermissions["browser_url_policy.view"],
            UserPermissions["access_requests.view"],
            UserPermissions["access_requests.update"],
          ]) && Boolean(this.userStore.isInternalUser),
        category: "securityCompliance",
        children: [
          {
            testId: "/tuvis-browser/policies",
            to: `/tuvis-browser/policies`,
            icon: LuShieldCheck,
            title: this.t("browser_url_policies_page.title"),
            permission:
              hasAnyPermission(this.userStore.user, [UserPermissions["browser_url_policy.update"], UserPermissions["browser_url_policy.view"]]) &&
              Boolean(this.userStore.isInternalUser),
          },
          {
            testId: "/tuvis-browser/settings",
            to: `/tuvis-browser/settings`,
            iconName: "settings" as TuvisIconNamesType,
            title: this.t("browser_settings_page.title"),
            permission:
              hasAnyPermission(this.userStore.user, [UserPermissions["browser_controls.view"], UserPermissions["browser_controls.update"]]) &&
              Boolean(this.userStore.isInternalUser),
          },
          {
            testId: "/tuvis-browser/applications",
            to: `/tuvis-browser/applications`,
            icon: IoApps,
            title: this.t("applications_page.title"),
            permission:
              hasAnyPermission(this.userStore.user, [UserPermissions["browser_url_policy.view"], UserPermissions["browser_url_policy.update"]]) &&
              Boolean(this.userStore.isInternalUser),
          },
          {
            testId: "/tuvis-browser/device-posture",
            to: `/tuvis-browser/device-posture`,
            icon: LuLaptopMinimalCheck,
            title: this.t("device_posture_page.title"),
            permission:
              hasAnyPermission(this.userStore.user, [UserPermissions["browser_controls.view"], UserPermissions["browser_controls.update"]]) &&
              Boolean(this.userStore.isInternalUser),
          },
          {
            testId: "/tuvis-browser/devices",
            to: `/tuvis-browser/devices`,
            icon: LuHardDrive,
            title: this.t("devices_page.title"),
            permission:
              hasAnyPermission(this.userStore.user, [UserPermissions["browser_controls.view"], UserPermissions["browser_controls.update"]]) &&
              Boolean(this.userStore.isInternalUser),
          },
          {
            testId: "/tuvis-browser/extensions",
            to: `/tuvis-browser/extensions`,
            icon: LuPuzzle,
            title: this.t("extensions_page.title"),
            permission:
              hasAnyPermission(this.userStore.user, [UserPermissions["browser_controls.view"], UserPermissions["browser_controls.update"]]) &&
              Boolean(this.userStore.isInternalUser),
          },
          {
            testId: "/tuvis-browser/saas",
            to: `/tuvis-browser/saas`,
            icon: LuCloud,
            title: this.t("saas_page.title"),
            permission:
              hasAnyPermission(this.userStore.user, [UserPermissions["browser_controls.view"], UserPermissions["browser_controls.update"]]) &&
              Boolean(this.userStore.isInternalUser),
          },
          {
            testId: "/tuvis-browser/customization",
            to: `/tuvis-browser/customization`,
            icon: RiPagesFill,
            title: this.t("customization_page.title"),
            permission:
              hasAnyPermission(this.userStore.user, [UserPermissions["browser_url_policy.view"], UserPermissions["browser_url_policy.update"]]) &&
              Boolean(this.userStore.isInternalUser),
          },
          {
            testId: "/tuvis-browser/access-requests",
            to: `/tuvis-browser/access-requests`,
            icon: IoCheckmarkDoneCircleOutline,
            title: this.t("access_requests_page.title"),
            permission:
              hasAnyPermission(this.userStore.user, [UserPermissions["access_requests.view"], UserPermissions["access_requests.update"]]) &&
              Boolean(this.userStore.isInternalUser),
          },
          {
            testId: "/tuvis-browser/notifications",
            to: `/tuvis-browser/notifications`,
            iconName: "notifications-bell" as TuvisIconNamesType,
            title: this.t("browser_notifications_page.title"),
            permission:
              hasAnyPermission(this.userStore.user, [UserPermissions["access_requests.view"], UserPermissions["access_requests.update"]]) &&
              Boolean(this.userStore.isInternalUser),
          },
        ],
      },
      {
        testId: "/dlp",
        to: `/dlp`,
        iconName: "block-shield" as TuvisIconNamesType,
        title: this.t("dlp_page.title"),
        defaultQuickAction: true,
        permission: hasAnyPermission(this.userStore.user, [
          UserPermissions["dlp.update"],
          UserPermissions["dlp.view"],
          UserPermissions["incident.update"],
          UserPermissions["incident.view"],
          UserPermissions["dlp.view_analytics"],
        ]),
        category: "securityCompliance",
        children: (() => {
          const cfg = this.orgConfigsStore.configs;
          const configReady = cfg != null;
          const whenReady = (p: boolean) => (configReady ? p : true);
          const user = this.userStore.user;
          return [
            {
              testId: "/dlp/policies",
              to: "/dlp/policies",
              icon: LuShieldCheck,
              title: this.t("dlp_page.tabs.policies"),
              permission: hasAnyPermission(user, [UserPermissions["dlp.update"], UserPermissions["dlp.view"]]),
            },
            {
              testId: "/dlp/rules",
              to: "/dlp/rules",
              icon: LuListChecks,
              title: this.t("dlp_page.tabs.rules"),
              permission: hasAnyPermission(user, [UserPermissions["dlp.update"], UserPermissions["dlp.view"]]),
            },
            {
              testId: "/dlp/incidents",
              to: "/dlp/incidents",
              icon: LuTriangleAlert,
              title: this.t("dlp_page.incidents"),
              permission: hasAnyPermission(user, [UserPermissions["incident.update"], UserPermissions["incident.view"]]),
            },
            {
              testId: "/dlp/analytics",
              to: "/dlp/analytics",
              icon: LuChartBar,
              title: this.t("analytics.title"),
              permission: whenReady(hasAnyPermission(user, [UserPermissions["dlp.view_analytics"]])),
            },
            {
              testId: "/dlp/incidents-summary-email/dlp",
              to: "/dlp/incidents-summary-email/dlp",
              icon: LuMail,
              title: this.t("incidents_summary_email.title"),
              permission: hasAnyPermission(user, [UserPermissions["incident.view"], UserPermissions["incident.update"]]),
            },
          ];
        })(),
      },
      {
        testId: "/surveillance",
        to: `/surveillance`,
        iconName: "message-search" as TuvisIconNamesType,
        title: this.t("surveillance_page.title"),
        permission: hasAnyPermission(this.userStore.user, [
          UserPermissions["surveillance.view"],
          UserPermissions["surveillance.update"],
          UserPermissions["surveillance.view_incidents"],
          UserPermissions["surveillance.view_analytics"],
        ]),
        category: "securityCompliance",
        children: (() => {
          const cfg = this.orgConfigsStore.configs;
          const configReady = cfg != null;
          const whenReady = (p: boolean) => (configReady ? p : true);
          const user = this.userStore.user;
          return [
            {
              testId: "/surveillance/policies",
              to: "/surveillance/policies",
              icon: LuShieldCheck,
              title: this.t("surveillance_page.tabs.policies"),
              permission: hasAnyPermission(user, [UserPermissions["surveillance.view"], UserPermissions["surveillance.update"]]),
            },
            {
              testId: "/surveillance/rules",
              to: "/surveillance/rules",
              icon: LuListChecks,
              title: this.t("surveillance_page.tabs.rules"),
              permission: hasAnyPermission(user, [UserPermissions["surveillance.view"], UserPermissions["surveillance.update"]]),
            },
            {
              testId: "/surveillance/incidents",
              to: "/surveillance/incidents",
              icon: LuTriangleAlert,
              title: this.t("surveillance_page.tabs.incidents"),
              permission: hasAnyPermission(user, [UserPermissions["surveillance.view_incidents"]]),
            },
            {
              testId: "/surveillance/analytics",
              to: "/surveillance/analytics",
              icon: LuChartBar,
              title: this.t("analytics.title"),
              permission: whenReady(Boolean(cfg?.enable_surveillance && hasAnyPermission(user, [UserPermissions["surveillance.view_analytics"]]))),
            },
            {
              testId: "/surveillance/incidents-summary-email/surveillance",
              to: "/surveillance/incidents-summary-email/surveillance",
              icon: LuMail,
              title: this.t("incidents_summary_email.title"),
              permission: hasAnyPermission(user, [UserPermissions["surveillance.view_incidents"], UserPermissions["surveillance.update_incidents"]]),
            },
          ];
        })(),
      },
      {
        testId: "/fraud-detection",
        to: `/fraud-detection`,
        icon: FaUserSecret,
        title: this.t("fraud_detection_page.title"),
        permission: hasAnyPermission(this.userStore.user, [
          UserPermissions["fraud.view"],
          UserPermissions["fraud.update"],
          UserPermissions["fraud.view_urls"],
          UserPermissions["fraud.update_urls"],
          UserPermissions["fraud.view_incidents"],
          UserPermissions["blocked_phones.view"],
          UserPermissions["blocked_phones.update"],
        ]),
        category: "securityCompliance",
        children: [
          {
            testId: "/fraud-detection/hashes",
            to: "/fraud-detection/hashes",
            icon: LuHash,
            title: this.t("fraud_detection_page.tabs.hashes"),
            permission: hasAnyPermission(this.userStore.user, [UserPermissions["fraud.view"], UserPermissions["fraud.update"]]),
          },
          {
            testId: "/fraud-detection/urls",
            to: "/fraud-detection/urls",
            icon: LuLink,
            title: this.t("fraud_detection_page.tabs.urls"),
            permission: hasAnyPermission(this.userStore.user, [UserPermissions["fraud.view_urls"], UserPermissions["fraud.update_urls"]]),
          },
          {
            testId: "/fraud-detection/incidents",
            to: "/fraud-detection/incidents",
            icon: LuShieldAlert,
            title: this.t("fraud_detection_page.tabs.incidents"),
            permission: hasAnyPermission(this.userStore.user, [UserPermissions["fraud.view_incidents"], UserPermissions["fraud.update_incidents"]]),
          },
          {
            testId: "/fraud-detection/incidents-summary-email/fraud",
            to: "/fraud-detection/incidents-summary-email/fraud",
            icon: LuMail,
            title: this.t("incidents_summary_email.title"),
            permission: hasAnyPermission(this.userStore.user, [UserPermissions["fraud.view_incidents"], UserPermissions["fraud.update_incidents"]]),
          },
          {
            testId: "/fraud-detection/blocked-phones",
            to: "/fraud-detection/blocked-phones",
            icon: LuPhoneOff,
            title: this.t("blocked_phones_settings_page.title"),
            permission: hasAnyPermission(this.userStore.user, [UserPermissions["blocked_phones.view"], UserPermissions["blocked_phones.update"]]),
          },
        ],
      },
      {
        testId: "/analytics/messages",
        to: "/analytics/messages",
        iconName: "messages-platform" as TuvisIconNamesType,
        title: this.t("messages_analytics_page.title"),
        permission: this.orgStore.isSameOrganization && hasAnyPermission(this.userStore.user, [UserPermissions["messages_analytics.view"]]),
        category: "communication",
      },
      {
        testId: "/campaigns",
        to: "/campaigns",
        icon: FaBullhorn,
        title: this.t("campaigns.title"),
        permission: hasAnyPermission(this.userStore.user, [UserPermissions["waba.view_settings"], UserPermissions["waba.update_settings"]]),
        category: "communication",
        children: [
          {
            testId: "/campaigns/list",
            to: "/campaigns/list",
            icon: LuList,
            title: this.t("campaigns.campaigns_tab"),
            permission: hasAnyPermission(this.userStore.user, [UserPermissions["waba.view_settings"], UserPermissions["waba.update_settings"]]),
          },
          {
            testId: "/campaigns/templates",
            to: "/campaigns/templates",
            icon: LuLayoutTemplate,
            title: this.t("campaigns.templates_tab"),
            permission: hasAnyPermission(this.userStore.user, [UserPermissions["waba.view_settings"], UserPermissions["waba.update_settings"]]),
          },
        ],
      },
      {
        testId: "/channels",
        to: "/channels",
        icon: FaMobile,
        title: this.t("channels.title"),
        defaultQuickAction: true,
        permission: hasAnyPermission(this.userStore.user, [UserPermissions["sidecars.view"]]),
        category: "communication",
        areChildrenLoading: () => this.channelsPageStore.init.state === "pending" || !this.channelsPageStore.initialized,
        children: (() => {
          const remoteSyncChannels = remoteSyncChannelsConfig.map((channel) => ({
            testId: channel.route,
            to: channel.route,
            title: channel.displayName,
            permission: hasAnyPermission(this.userStore.user, [UserPermissions["sidecars.view"], UserPermissions["sidecars.update"]]),
            category: "tuvis-mobile",
            icon: channel.icon,
            disabled: !this.channelsPageStore.isSidecarChannelAvailable(channel.channelKey),
            disabledTooltip: this.channelsPageStore.getRemoteSyncUnavailableTooltip(channel.displayName),
          }));

          remoteSyncChannels.sort((a, b) => Number(Boolean(a.disabled)) - Number(Boolean(b.disabled)));

          return [
            ...remoteSyncChannels,
            {
              testId: "/channels/mobile/wechat",
              to: "/channels/mobile/wechat",
              title: "WeChat",
              permission: true,
              category: "tuvis-mobile",
              icon: IoLogoWechat,
              hidden: true,
            },
            {
              testId: "/channels/sidecar-notifications",
              to: "/channels/sidecar-notifications",
              title: this.t("sidecarNotifications.title"),
              permission: hasAnyPermission(this.userStore.user, [UserPermissions["sidecars.notifications"]]),
              category: "notifications",
              iconName: "notifications-bell" as TuvisIconNamesType,
            },
            {
              testId: "/channels/sidecar-customizations",
              to: "/channels/sidecar-customizations",
              title: this.t("sidecarCustomizations.title"),
              permission: hasAnyPermission(this.userStore.user, [UserPermissions["sidecars.notifications"]]),
              category: "notifications",
              icon: LuSettings,
            },
            {
              testId: "/channels/analytics",
              to: "/channels/analytics",
              title: this.t("analytics.title"),
              permission: (() => {
                const cfg = this.orgConfigsStore.configs;
                const configReady = cfg != null;
                const whenReady = (p: boolean) => (configReady ? p : true);
                return whenReady(Boolean(cfg?.analytics_compliance && hasAnyPermission(this.userStore.user, [UserPermissions["compliance.view"]])));
              })(),
              category: "notifications",
              iconName: "engagement" as TuvisIconNamesType,
            },
          ];
        })(),
      },
      {
        testId: "/chat/agent",
        to: "/chat/agent",
        iconName: "chat-now" as TuvisIconNamesType,
        title: this.t("whatsapp_business_api_agent_page.title"),
        permission: Boolean(this.userStore.user?.channels?.waba?.length || this.userStore.user?.channels?.twilio?.length),
        category: "communication",
      },
      {
        testId: "/bots",
        to: "/bots",
        icon: LuBot,
        title: this.t("bot_builder.title"),
        permission: hasAnyPermission(this.userStore.user, [UserPermissions["bot_builder.view"], UserPermissions["bot_builder.update"]]),
        category: "communication",
        children: [
          {
            testId: "/bots/flows",
            to: "/bots/flows",
            icon: LuWorkflow,
            title: this.t("bot_builder.nav_flows"),
            permission: hasAnyPermission(this.userStore.user, [UserPermissions["bot_builder.view"], UserPermissions["bot_builder.update"]]),
          },
          {
            testId: "/bots/analytics",
            to: "/bots/analytics",
            icon: LuChartBar,
            title: this.t("bot_builder.nav_analytics"),
            permission: hasAnyPermission(this.userStore.user, [UserPermissions["bot_builder.view"]]),
          },
          {
            testId: "/bots/sessions",
            to: "/bots/sessions",
            icon: LuList,
            title: this.t("bot_builder.nav_sessions"),
            permission: hasAnyPermission(this.userStore.user, [UserPermissions["bot_builder.view"]]),
          },
          {
            testId: "/bots/triggers",
            to: "/bots/triggers",
            icon: LuSend,
            title: this.t("bot_builder.nav_triggers"),
            permission: hasAnyPermission(this.userStore.user, [UserPermissions["bot_builder.view_triggers"]]),
          },
          {
            testId: "/bots/credentials",
            to: "/bots/credentials",
            icon: LuKeyRound,
            title: this.t("bot_builder.nav_credentials"),
            permission: hasAnyPermission(this.userStore.user, [UserPermissions["bot_builder.view"], UserPermissions["bot_builder.update"]]),
          },
          {
            testId: "/bots/settings",
            to: "/bots/settings",
            icon: LuSettings,
            title: this.t("bot_builder.nav_settings"),
            permission: hasAnyPermission(this.userStore.user, [UserPermissions["bot_builder.view"], UserPermissions["bot_builder.update"]]),
          },
        ],
      },
      {
        testId: "/keyword-insights",
        to: `/keyword-insights`,
        iconName: "message-search" as TuvisIconNamesType,
        title: this.t("keyword_insights.title"),
        permission: hasAnyPermission(this.userStore.user, [
          UserPermissions["keyword_insights.view"],
          UserPermissions["keyword_insights.view_analytics"],
        ]),
        category: "communication",
        defaultQuickAction: true,
        children: (() => {
          const cfg = this.orgConfigsStore.configs;
          const configReady = cfg != null;
          const whenReady = (p: boolean) => (configReady ? p : true);
          const user = this.userStore.user;
          return [
            {
              testId: "/keyword-insights/campaigns",
              to: "/keyword-insights/campaigns",
              icon: LuMegaphone,
              title: this.t("keyword_insights.campaigns_page.title"),
              permission: hasAnyPermission(user, [UserPermissions["keyword_insights.view"]]),
            },
            {
              testId: "/keyword-insights/occurrences",
              to: "/keyword-insights/occurrences",
              icon: LuSearch,
              title: this.t("keyword_insights.occurrences_page.title"),
              permission: hasAnyPermission(user, [UserPermissions["keyword_insights.view"]]),
            },
            {
              testId: "/keyword-insights/analytics",
              to: "/keyword-insights/analytics",
              icon: LuChartBar,
              title: this.t("analytics.title"),
              permission: whenReady(
                Boolean(cfg?.allow_keyword_insights && hasAnyPermission(user, [UserPermissions["keyword_insights.view_analytics"]]))
              ),
            },
          ];
        })(),
      },
      {
        testId: "/groups-metadata",
        to: `/groups-metadata`,
        icon: FaWhatsapp,
        title: this.t("group_metadata.title"),
        permission: hasAnyPermission(this.userStore.user, [UserPermissions["im_groups.view"]]),
        category: "communication",
      },
      {
        testId: "/install/desktop",
        to: "/install/desktop",
        iconName: "download" as TuvisIconNamesType,
        title: this.t("download_tuvispace_page.title"),
        permission: this.hasDesktopPermission,
        hidden: true,
      },
      {
        testId: "/api-docs",
        to: "/api-docs",
        title: this.t("api_docs_settings_page.title"),
        permission: hasAnyPermission(this.userStore.user, [UserPermissions["api_keys.view"]]),
        hidden: true,
      },
      {
        testId: "/tuvis-contacts",
        to: `/tuvis-contacts`,
        iconName: "contacts" as TuvisIconNamesType,
        title: this.t("tuvis_contacts_page.title"),
        permission: hasAnyPermission(this.userStore.user, [UserPermissions["tuvis_contacts.update"], UserPermissions["tuvis_contacts.view"]]),
        category: "communication",
      },
      {
        testId: "/im-templates",
        to: `/im-templates`,
        icon: LuLayoutTemplate,
        title: this.t("im_templates.title"),
        permission: hasAnyPermission(this.userStore.user, [UserPermissions["im_templates.view"], UserPermissions["im_templates.create"]]),
        category: "communication",
      },
      {
        testId: "/chat/destinations",
        to: `/chat/destinations`,
        icon: GrAppsRounded,
        title: this.t("destinations_page.title"),
        permission: hasAnyPermission(this.userStore.user, [
          UserPermissions["destinations.view"],
          UserPermissions["waba.view_settings"],
          UserPermissions["waba.update_settings"],
          UserPermissions["twilio.view_settings"],
          UserPermissions["twilio.update_settings"],
          UserPermissions["slack.view_settings"],
          UserPermissions["slack.update_settings"],
          UserPermissions["teams.view_settings"],
          UserPermissions["teams.update_settings"],
          UserPermissions["archive.view_settings"],
          UserPermissions["archive.update_settings"],
        ]),
        category: "system",
        children: (() => {
          const DESTINATION_INTEGRATION_BASE = "/chat/destinations/integration";

          const DESTINATION_INTEGRATION_TAB_ORDER = [
            DESTINATION_INTEGRATION_TYPES.MESSAGES,
            DESTINATION_INTEGRATION_TYPES.AUDIO_TRANSCRIBES,
            DESTINATION_INTEGRATION_TYPES.AUDIT_LOGS,
            DESTINATION_INTEGRATION_TYPES.DLP_INCIDENTS,
            DESTINATION_INTEGRATION_TYPES.DLP_DETECTORS,
            DESTINATION_INTEGRATION_TYPES.LLM_PROVIDER,
            DESTINATION_INTEGRATION_TYPES.PLATFORMS_CHANNELS,
            DESTINATION_INTEGRATION_TYPES.CHANNELS,
            DESTINATION_INTEGRATION_TYPES.ARCHIVE_CHANNELS,
            DESTINATION_INTEGRATION_TYPES.FILE_SCANNING,
            DESTINATION_INTEGRATION_TYPES.SECURITY,
          ] as const;
          const cfg = this.orgConfigsStore.configs;
          const configReady = cfg != null;
          const whenReady = (p: boolean) => (configReady ? p : true);
          type DestinationIntegrationTab = (typeof DESTINATION_INTEGRATION_TAB_ORDER)[number];
          const destinationIntegrationTabAllowed = {
            [DESTINATION_INTEGRATION_TYPES.MESSAGES]: true,
            [DESTINATION_INTEGRATION_TYPES.AUDIO_TRANSCRIBES]: whenReady(Boolean(cfg?.allow_audio_transcribes || cfg?.allow_bot_builder)),
            [DESTINATION_INTEGRATION_TYPES.AUDIT_LOGS]: whenReady(Boolean(cfg?.access_audit_log)),
            [DESTINATION_INTEGRATION_TYPES.DLP_INCIDENTS]: whenReady(Boolean(cfg?.allow_dlp)),
            [DESTINATION_INTEGRATION_TYPES.DLP_DETECTORS]: true,
            [DESTINATION_INTEGRATION_TYPES.FILE_SCANNING]: whenReady(Boolean(cfg?.fraud_detection)) && Boolean(this.userStore.isInternalUser),
            [DESTINATION_INTEGRATION_TYPES.SECURITY]: true,
            [DESTINATION_INTEGRATION_TYPES.LLM_PROVIDER]: whenReady(
              Boolean(cfg?.enable_widget_ai_assistant || cfg?.show_chat_summarize_action || cfg?.allow_bot_builder)
            ),
            [DESTINATION_INTEGRATION_TYPES.PLATFORMS_CHANNELS]: whenReady(
              Boolean(
                hasAnyPermission(this.userStore.user, [
                  UserPermissions["slack.view_settings"],
                  UserPermissions["slack.update_settings"],
                  UserPermissions["teams.view_settings"],
                  UserPermissions["teams.update_settings"],
                ])
              )
            ),
            [DESTINATION_INTEGRATION_TYPES.CHANNELS]: whenReady(
              Boolean(
                hasAnyPermission(this.userStore.user, [
                  UserPermissions["destinations.view"],
                  UserPermissions["destinations.update"],
                  UserPermissions["waba.view_settings"],
                  UserPermissions["waba.update_settings"],
                  UserPermissions["twilio.view_settings"],
                  UserPermissions["twilio.update_settings"],
                ])
              )
            ),
            [DESTINATION_INTEGRATION_TYPES.ARCHIVE_CHANNELS]: whenReady(
              Boolean(hasAnyPermission(this.userStore.user, [UserPermissions["archive.view_settings"], UserPermissions["archive.update_settings"]]))
            ),
          } satisfies Record<DestinationIntegrationTab, boolean>;
          const destinationIntegrationTabIcon = {
            [DESTINATION_INTEGRATION_TYPES.MESSAGES]: LuMessageSquare,
            [DESTINATION_INTEGRATION_TYPES.AUDIO_TRANSCRIBES]: LuMic,
            [DESTINATION_INTEGRATION_TYPES.AUDIT_LOGS]: LuScrollText,
            [DESTINATION_INTEGRATION_TYPES.DLP_INCIDENTS]: LuTriangleAlert,
            [DESTINATION_INTEGRATION_TYPES.DLP_DETECTORS]: LuShieldCheck,
            [DESTINATION_INTEGRATION_TYPES.FILE_SCANNING]: LuFileScan,
            [DESTINATION_INTEGRATION_TYPES.SECURITY]: LuLock,
            [DESTINATION_INTEGRATION_TYPES.LLM_PROVIDER]: LuSparkles,
            [DESTINATION_INTEGRATION_TYPES.PLATFORMS_CHANNELS]: LuMessagesSquare,
            [DESTINATION_INTEGRATION_TYPES.CHANNELS]: LuRadio,
            [DESTINATION_INTEGRATION_TYPES.ARCHIVE_CHANNELS]: LuArchive,
          } satisfies Record<DestinationIntegrationTab, IconType>;
          return DESTINATION_INTEGRATION_TAB_ORDER.map((tab) => ({
            testId: `${DESTINATION_INTEGRATION_BASE}/${tab}`,
            to: `${DESTINATION_INTEGRATION_BASE}/${tab}`,
            icon: destinationIntegrationTabIcon[tab],
            title: this.t(`destinations_page.tabs.${tab}`),
            permission: hasAnyPermission(this.userStore.user, [UserPermissions["destinations.view"]]) && destinationIntegrationTabAllowed[tab],
          }));
        })(),
      },
      {
        testId: "/account",
        to: `/account`,
        icon: RiUserSettingsLine,
        title: this.t("account_admin.title"),
        permission: this.userStore.hasAccountAdmin || this.userStore.isAnyAdmin,
        category: "system",
        children: [
          {
            testId: "/account/overview",
            to: `/account/overview`,
            iconName: "settings" as TuvisIconNamesType,
            title: this.t("account_admin.overview_page.title"),
            permission: this.userStore.hasAccountAdmin || this.userStore.isAnyAdmin,
          },
          {
            testId: "/account/users",
            to: `/account/users`,
            iconName: "action-new-lead-contact" as TuvisIconNamesType,
            title: this.t("account_admin.users_page.title"),
            permission: this.userStore.hasAccountAdmin || this.userStore.isAnyAdmin,
          },
        ],
      },
      {
        testId: "/settings",
        to: "/settings",
        icon: LuSettings,
        title: this.t("settings"),
        permission: true,
        category: "system",
        defaultQuickAction: true,
        children: [
          {
            testId: "/settings/org-configs/tuvisAdminChannels",
            to: `/settings/org-configs/tuvisAdminChannels`,
            title: this.t("super_admin_org_configs.title.tuvisAdminChannels"),
            permission: this.userStore.isInternalUser,
            category: "tuvisAdmin",
          },
          {
            testId: "/settings/org-configs/tuvisAdminFeatures",
            to: `/settings/org-configs/tuvisAdminFeatures`,
            title: this.t("super_admin_org_configs.title.tuvisAdminFeatures"),
            permission: this.userStore.isInternalUser,
            category: "tuvisAdmin",
          },
          {
            testId: "/settings/org-configs/tuvisAdminMessagesSync",
            to: `/settings/org-configs/tuvisAdminMessagesSync`,
            title: this.t("super_admin_org_configs.title.tuvisAdminMessagesSync"),
            permission: this.userStore.isInternalUser,
            category: "tuvisAdmin",
          },
          {
            testId: "/settings/org-configs/tuvisAdminSecurity",
            to: `/settings/org-configs/tuvisAdminSecurity`,
            title: this.t("super_admin_org_configs.title.tuvisAdminSecurity"),
            permission: this.userStore.isInternalUser,
            category: "tuvisAdmin",
          },
          {
            testId: "/settings/notifications",
            to: `/settings/notifications`,
            title: this.t("super_admin_org_configs.nav.system_notifications"),
            permission: hasAnyPermission(this.userStore.user, [UserPermissions["notifications.view"]]),
            category: "tuvisAdmin",
          },
          {
            testId: "/settings/api-keys",
            to: `/settings/api-keys`,
            title: this.t("super_admin_org_configs.nav.api_keys"),
            permission: hasAnyPermission(this.userStore.user, [UserPermissions["api_keys.view"]]),
            category: "tuvisAdmin",
          },
          {
            testId: "/settings/elite-token",
            to: `/settings/elite-token`,
            title: this.t("super_admin_org_configs.nav.elite_token"),
            permission: hasAnyPermission(this.userStore.user, [UserPermissions["elite_token.view"]]),
            category: "orgAdmin",
          },
          {
            testId: `/settings/saml`,
            to: `/settings/saml`,
            title: this.t("super_admin_org_configs.nav.sso_settings"),
            permission: hasAnyPermission(this.userStore.user, [UserPermissions["saml.view"]]),
            category: "tuvisAdmin",
          },
          {
            testId: "/settings/org-configs/actions",
            to: `/settings/org-configs/actions`,
            title: "Actions",
            permission:
              this.userStore.isInternalUser ||
              hasAnyPermission(this.userStore.user, [UserPermissions["settings.view_general"], UserPermissions["settings.update_general"]]),
            category: "widget",
          },
          {
            testId: "/settings/org-configs/apps",
            to: `/settings/org-configs/apps`,
            title: "Apps",
            permission:
              this.userStore.isInternalUser ||
              hasAnyPermission(this.userStore.user, [UserPermissions["settings.view_general"], UserPermissions["settings.update_general"]]),
            category: "widget",
          },
          {
            testId: "/settings/global-layout",
            to: `/settings/global-layout`,
            title: this.t("globalLayout.title"),
            permission: hasAnyPermission(this.userStore.user, [UserPermissions["global_layout.view"]]),
            category: "widget",
            hidden: !this.orgStore.isCrmOrg,
          },
          {
            testId: "/settings/dashboards",
            to: "/settings/dashboards",
            icon: LuLayoutDashboard,
            title: this.t("dashboards.title"),
            permission: hasAnyPermission(this.userStore.user, [UserPermissions["dashboards.view"]]),
            category: "crm",
          },
          {
            testId: "/settings/custom-filters",
            to: "/settings/custom-filters",
            icon: LuFilter,
            title: this.t("custom_filters.title"),
            permission: this.orgStore.isCrmOrg && hasAnyPermission(this.userStore.user, [UserPermissions["custom_filters.view"]]),
            category: "crm",
          },
          {
            testId: "/settings/org-configs/entity",
            to: `/settings/org-configs/entity`,
            title: "Entity",
            permission: this.userStore.isInternalUser,
            category: "crm",
          },
          {
            testId: "/settings/org-configs/autoSyncCases",
            to: `/settings/org-configs/autoSyncCases`,
            title: this.t("super_admin_org_configs.title.autoSyncCases"),
            permission: this.userStore.isInternalUser,
            category: "crm",
          },
          {
            testId: "/settings/org-configs/autoSyncActivities",
            to: `/settings/org-configs/autoSyncActivities`,
            title: this.t("super_admin_org_configs.title.autoSyncActivities"),
            permission: this.userStore.isInternalUser,
            category: "crm",
          },
          {
            testId: "/settings/org-configs/layoutLimits",
            to: `/settings/org-configs/layoutLimits`,
            title: this.t("super_admin_org_configs.title.layoutLimits"),
            permission: this.userStore.isInternalUser || import.meta.env.MODE !== "production",
            category: "crm",
          },
          {
            testId: "/settings/sf-email-domain-map",
            to: `/settings/sf-email-domain-map`,
            title: "Sf Email Domains",
            permission: this.userStore.isInternalUser,
            category: "crm",
          },
          {
            testId: "/settings/org-configs/general",
            to: `/settings/org-configs/general`,
            title: this.t("super_admin_org_configs.title.general"),
            permission: hasAnyPermission(this.userStore.user, [UserPermissions["settings.view_general"]]),
            category: "system",
          },
          {
            testId: "/settings/org-configs/orgAdminMessageSync",
            to: `/settings/org-configs/orgAdminMessageSync`,
            title: this.t("super_admin_org_configs.title.orgAdminMessageSync"),
            permission: this.userStore.isInternalUser,
            category: "orgAdmin",
          },
          {
            testId: "/settings/org-configs/orgAdminRules",
            to: `/settings/org-configs/orgAdminRules`,
            title: this.t("super_admin_org_configs.title.orgAdminRules"),
            permission: this.userStore.isInternalUser,
            category: "orgAdmin",
          },
          {
            testId: "/settings/org-configs/orgAdminUserManagement",
            to: `/settings/org-configs/orgAdminUserManagement`,
            title: this.t("super_admin_org_configs.title.orgAdminUserManagement"),
            permission: this.userStore.isInternalUser,
            category: "orgAdmin",
          },
          {
            testId: "/settings/phones-whitelist",
            to: `/settings/phones-whitelist`,
            title: this.t("phone_whitelist_settings_page.title"),
            permission: hasAnyPermission(this.userStore.user, [UserPermissions["phones_whitelist.view"]]),
            category: "feature",
          },
          {
            testId: "/settings/phones-blocklist",
            to: `/settings/phones-blocklist`,
            title: this.t("phone_blocklist_settings_page.title"),
            permission: hasAnyPermission(this.userStore.user, [UserPermissions["phones_blocklist.view"]]),
            category: "feature",
          },
        ],
      },
      // Internal Admin Tools Section (bottom of sidebar with visual divider)
      {
        testId: "/admin/all-users",
        to: `/admin/all-users`,
        icon: FaPeopleGroup,
        title: this.t("internal_admin_tools.global_user_search"),
        permission: (this.userStore.isAdmin || this.userStore.isSuperAdmin) && import.meta.env.MODE !== "test",
        category: "internalAdminTools",
        separatedSection: true,
      },
      {
        testId: "/admin/tools/org-explorer",
        to: `/admin/tools/org-explorer`,
        icon: LuLayoutList,
        title: this.t("internal_admin_tools.org_explorer"),
        permission: this.userStore.isAdmin || this.userStore.isSuperAdmin,
        category: "internalAdminTools",
        separatedSection: true,
      },
      {
        testId: "/admin/tools/org-overview",
        to: `/admin/tools/org-overview`,
        iconName: "globe" as TuvisIconNamesType,
        title: this.t("org_overview.title"),
        permission: this.userStore.isInternalUser && import.meta.env.MODE !== "test",
        category: "internalAdminTools",
        separatedSection: true,
      },
    ]);
  }

  get availablePaths() {
    return this.tree.items.flatMap((option) => [option, ...(option.subtree?.items ?? [])]).map((option) => option.to);
  }
}
